// [목적] 폴리오 댓글 목록(무한스크롤) + 작성/수정/삭제 mutation 통합 훅 — useComments 위의 얇은 래퍼
// [이유] fe-folio-social-wire — FolioPage/FolioMobile 공용 CommentSection 컴포넌트가 사용.
//         [수정: 2026-09-21] festival-comments Spec D-8/OQ-4 — 본문 로직을 어댑터 기반 범용 훅 useComments 로 승격하고
//         이 파일은 "폴리오 어댑터 생성 + 시그니처/반환 타입 유지 래퍼"로 강등했다. 축제 댓글이 같은 구현을 공유한다.
//         GET /folios/:id/comments 는 parentId 미지정 시 최상위+대댓글이 모두 평탄하게 반환되므로 평탄한 시간순 목록으로 다룬다.
// [사이드 이팩트] create/patch/delete 성공 시 queryKeys.folioSocial.comments(folioId) prefix invalidate(useComments 담당)
//                 → 페이지 파라미터 무관 모든 캐시 엔트리 재조회. 폴리오 요청 경로·queryKey 는 리팩터 전과 동일.
// [수정 시 고려사항] 어댑터 create 는 입력 객체를 addComment(folioId, input) 로 그대로 통과시켜야 한다(TR-9) —
//                   CommentSection.test 가 addComment(FOLIO_ID,{text}) 호출 형태를 단언한다.

import type { FolioComment } from '@gangwoncanvas/types';

import * as folioSocialClient from '../api/folio-social.client.js';
import { queryKeys } from '../api/queryKeys.js';

import { useComments, type CommentTargetAdapter, type UseCommentsResult } from './useComments.js';

export type UseFolioCommentsResult = UseCommentsResult<FolioComment>;

/**
 * [목적] 폴리오 댓글 어댑터 생성 — folio-social.client 4개 호출 + folioSocial queryKey 를 어댑터 계약에 매핑
 * [이유] FolioCommentSection(래퍼)·useFolioComments 두 곳이 같은 매핑을 공유하도록 단일 팩토리로 둔다.
 * [사이드 이팩트] 없음(순수 객체 생성) — 실제 네트워크 호출은 어댑터 메서드 실행 시점.
 * [수정 시 고려사항] commentIntent/reportTargetType 은 기존 CommentSection 하드코딩 값('folio.comment'/'comment')과 동일해야 한다.
 */
export function folioCommentAdapter(folioId: string): CommentTargetAdapter<FolioComment> {
  return {
    queryKey: queryKeys.folioSocial.comments(folioId),
    commentIntent: 'folio.comment',
    reportTargetType: 'comment',
    list: ({ cursor }) => folioSocialClient.getComments(folioId, { cursor }),
    create: (input) => folioSocialClient.addComment(folioId, input),
    patch: (commentId, text) => folioSocialClient.patchComment(folioId, commentId, { text }),
    remove: (commentId) => folioSocialClient.deleteComment(folioId, commentId),
  };
}

/**
 * [목적] 폴리오 댓글 훅 — 목록 무한스크롤 + 작성/수정/삭제 mutation
 * [이유] 기존 호출부/테스트 호환 — 시그니처·반환 타입 유지.
 * [사이드 이팩트] 각 mutation 성공 시 comments 목록 invalidate(전체 재조회) — 최신 상태 보장.
 * [수정 시 고려사항] folioId 는 항상 truthy 라고 가정(호출부가 폴리오 로딩 완료 후에만 마운트).
 */
export function useFolioComments(folioId: string): UseFolioCommentsResult {
  return useComments(folioCommentAdapter(folioId));
}
